import React, { useEffect } from 'react'
import {
  AnalysisDemographicsTab,
  AnalysisEmploymentTab,
  AnalysisTaxTab,
  AnalysisElectricityTab,
  AnalysisNoiseTab,
  AnalysisSunlightTab,
  AnalysisTransportAccessTab,
  AnalysisAmenitiesTab,
  AnalysisPurchaseMarketTab,
  AnalysisRentalMarketTab,
  AnalysisBuildingPermitsTab,
  AnalysisTransactionsTab,
  AnalysisListingsTab,
  AnalysisHistoryTab,
  AnalysisBuildingCharacteristicsTab,
  AnalysisEnergyTab,
  AnalysisBuildingSummaryTab,
  AnalysisUnitDetailsTab,
  AnalysisSectionThemeProvider,
  SectionNotAvailable,
  IconName,
  SectionColorKey,
} from '@popety-io/ui'
import {
  SharedFilesSection,
  SharedNotesSection,
} from '../../../components/SharedAttachments/SharedAttachments'
import { useMapSectionData, type SectionType } from '../contexts'

interface BuildingAnalysisContentProps {
  activeSection: string
  buildingData: any
  hash: string
}

// Helper to check if a section is available in the shared data
const isSectionAvailable = (buildingData: any, sectionId: string): boolean => {
  if (
    !buildingData.available_sections ||
    buildingData.available_sections.length === 0
  ) {
    return true
  }
  return buildingData.available_sections.includes(sectionId)
}

// Section theme mapping for colored header icons
type SectionTheme = { icon: IconName; sectionColor: SectionColorKey }
const sectionThemeMap: Record<string, SectionTheme> = {
  summary: { icon: 'bar-chart', sectionColor: 'steel' },
  overview: { icon: 'bar-chart', sectionColor: 'steel' },
  history: { icon: 'clock', sectionColor: 'steel' },
  'building-characteristics': { icon: 'cube', sectionColor: 'steel' },
  characteristics: { icon: 'cube', sectionColor: 'steel' },
  'unit-details': { icon: 'building-2', sectionColor: 'steel' },
  units: { icon: 'building-2', sectionColor: 'steel' },
  energy: { icon: 'battery-charging', sectionColor: 'steel' },
  'upper-elevation': { icon: 'trending-up', sectionColor: 'bronze' },
  elevation: { icon: 'trending-up', sectionColor: 'bronze' },
  'building-permits': { icon: 'building-permit', sectionColor: 'bronze' },
  permits: { icon: 'building-permit', sectionColor: 'bronze' },
  feasibility: { icon: 'lightbulb', sectionColor: 'bronze' },
  'feasibility-study': { icon: 'lightbulb', sectionColor: 'bronze' },
  'purchase-market': { icon: 'cash', sectionColor: 'forest' },
  'rental-market': { icon: 'home', sectionColor: 'forest' },
  'purchase-listings': { icon: 'listing-purchase', sectionColor: 'forest' },
  purchase: { icon: 'listing-purchase', sectionColor: 'forest' },
  'rental-listings': { icon: 'listing-rental', sectionColor: 'forest' },
  rental: { icon: 'listing-rental', sectionColor: 'forest' },
  transactions: { icon: 'real-estate-transaction', sectionColor: 'forest' },
  transport: { icon: 'car', sectionColor: 'deepBlue' },
  'transport-access': { icon: 'car', sectionColor: 'deepBlue' },
  amenities: { icon: 'store', sectionColor: 'deepBlue' },
  noise: { icon: 'activity', sectionColor: 'deepBlue' },
  'noise-analysis': { icon: 'activity', sectionColor: 'deepBlue' },
  sunlight: { icon: 'sun', sectionColor: 'deepBlue' },
  'sunlight-study': { icon: 'sun', sectionColor: 'deepBlue' },
  demographics: { icon: 'users', sectionColor: 'wine' },
  employment: { icon: 'briefcase', sectionColor: 'wine' },
  taxes: { icon: 'receipt', sectionColor: 'wine' },
  electricity: { icon: 'zap', sectionColor: 'wine' },
  notes: { icon: 'edit', sectionColor: 'steel' },
  files: { icon: 'file', sectionColor: 'steel' },
}

// Map navigation section IDs to map section types
const sectionToMapSection: Record<string, SectionType> = {
  'purchase-listings': 'purchase_listings',
  'rental-listings': 'rental_listings',
  transactions: 'transactions',
  'building-permits': 'building_permits',
  amenities: 'amenities',
  'transport-access': 'transport',
  transport: 'transport',
  demographics: 'demographics',
  employment: 'employment',
  history: 'history',
  energy: 'energy',
  sunlight: 'sunlight',
  'sunlight-study': 'sunlight_study',
  'purchase-market': 'purchase_market',
  'rental-market': 'rental_market',
  noise: 'noise',
  taxes: 'taxes',
  electricity: 'electricity',
}

export const BuildingAnalysisContent: React.FC<
  BuildingAnalysisContentProps
> = ({ activeSection, buildingData, hash }) => {
  const { setActiveSection, clearSectionData } = useMapSectionData()

  // Update map section when navigation section changes
  useEffect(() => {
    const mapSection = sectionToMapSection[activeSection]
    if (mapSection) {
      setActiveSection(mapSection)
    } else {
      setActiveSection(null)
      clearSectionData()
    }
  }, [activeSection, setActiveSection, clearSectionData])

  const renderContent = () => {
    // Helper to check availability and return not-available component
    const checkAvailability = (sectionId: string, sectionName: string) => {
      if (!isSectionAvailable(buildingData, sectionId)) {
        return <SectionNotAvailable sectionName={sectionName} />
      }
      return null
    }

    switch (activeSection) {
      case 'summary': {
        const na = checkAvailability('summary', 'Summary')
        return na || <AnalysisBuildingSummaryTab />
      }
      case 'history': {
        const na = checkAvailability('history', 'History')
        return na || <AnalysisHistoryTab />
      }
      case 'building-characteristics':
      case 'characteristics': {
        const na = checkAvailability(
          'characteristics',
          'Building Characteristics'
        )
        return na || <AnalysisBuildingCharacteristicsTab />
      }
      case 'unit-details':
      case 'units': {
        const na = checkAvailability('units', 'Unit Details')
        return na || <AnalysisUnitDetailsTab />
      }
      case 'energy': {
        const na = checkAvailability('energy', 'Energy')
        return na || <AnalysisEnergyTab />
      }
      case 'building-permits':
      case 'permits': {
        const na = checkAvailability('building_permits', 'Building Permits')
        return na || <AnalysisBuildingPermitsTab />
      }
      case 'purchase-market': {
        const na = checkAvailability('purchase_market', 'Purchase Market')
        return na || <AnalysisPurchaseMarketTab />
      }
      case 'rental-market': {
        const na = checkAvailability('rental_market', 'Rental Market')
        return na || <AnalysisRentalMarketTab />
      }
      case 'purchase-listings':
      case 'purchase': {
        const na = checkAvailability('purchase_listings', 'Purchase Listings')
        return na || <AnalysisListingsTab type="purchase" />
      }
      case 'rental-listings':
      case 'rental': {
        const na = checkAvailability('rental_listings', 'Rental Listings')
        return na || <AnalysisListingsTab type="rental" />
      }
      case 'transactions': {
        const na = checkAvailability('transactions', 'Transactions')
        return na || <AnalysisTransactionsTab />
      }
      case 'transport':
      case 'transport-access': {
        const na = checkAvailability('transport', 'Transport & Access')
        return na || <AnalysisTransportAccessTab />
      }
      case 'amenities': {
        const na = checkAvailability('amenities', 'Amenities')
        return na || <AnalysisAmenitiesTab />
      }
      case 'noise':
      case 'noise-analysis': {
        const na = checkAvailability('noise', 'Noise Analysis')
        return na || <AnalysisNoiseTab />
      }
      case 'sunlight':
      case 'sunlight-study': {
        const na = checkAvailability('sunlight', 'Sunlight Study')
        return na || <AnalysisSunlightTab />
      }
      case 'demographics': {
        const na = checkAvailability('demographics', 'Demographics')
        return na || <AnalysisDemographicsTab />
      }
      case 'employment': {
        const na = checkAvailability('employment', 'Employment')
        return na || <AnalysisEmploymentTab />
      }
      case 'taxes': {
        const na = checkAvailability('taxes', 'Taxes')
        return na || <AnalysisTaxTab />
      }
      case 'electricity': {
        const na = checkAvailability('electricity', 'Electricity')
        return na || <AnalysisElectricityTab />
      }
      case 'notes': {
        const na = checkAvailability('notes', 'Notes')
        return na || <SharedNotesSection notes={buildingData.notes} />
      }
      case 'files': {
        const na = checkAvailability('files', 'Files')
        return (
          na || (
            <SharedFilesSection
              files={buildingData.files}
              hash={hash}
              kind="building"
            />
          )
        )
      }
      default:
        return <AnalysisBuildingSummaryTab />
    }
  }

  const sectionTheme = sectionThemeMap[activeSection] || {}

  return (
    <AnalysisSectionThemeProvider value={sectionTheme}>
      {renderContent()}
    </AnalysisSectionThemeProvider>
  )
}
